/**
 * Tablas de subsección (`subTable` = subsection table) para incidente: definición, agregación y registro.
 *
 * Tres copias explícitas (misma lógica y mismos valores). Cualquier cambio en este archivo debe
 * replicarse en los otros dos:
 * - stem-web-backend/src/incidentSubTable.ts
 * - planiwise-frontend/src/modules/incident/incidentSubTable.ts (este)
 * - planiwise-app/functions/incidentSubTable.ts
 *
 * Incluye: `INCIDENT_SUBTABLE_DEFS`, constantes `INCIDENT_SUBTABLE_KIND_*`, plantillas de columna,
 * keyPointers, `incidentRowsPath`, overrides del tipo y funciones que definan el mismo contrato
 * entre cliente y servidor.
 *
 * Los módulos `incidentSubTableProcedureList` (pareja planiwise-frontend + planiwise-app) consumen
 * estas defs; si cambia cómo se itera o se hace prefill, mantener esa pareja alineada también.
 */

import {applyIncidentPrefillToPreguntaField} from 'src/modules/incident/incidentProcedurePrefill';

export type IncidentSubTableColumnTemplate = {
  id: string;
  defaultLabel: string;
  keyPointer: string;
  /** Propiedad en cada fila persistida en el incidente (p. ej. `name`, `statement`). */
  rowField: string;
  supportsPhotos?: boolean;
  /** Con `supportsPhotos`, array de adjuntos en la fila (p. ej. `statementImages`). Por defecto `${rowField}Images`. */
  rowPhotosField?: string;
  /** Si true, la columna no se incluye en la tabla del PDF de incidente (los datos se siguen guardando). */
  excludeFromIncidentPdf?: boolean;
  /** El editor de columnas solo la ofrece para Blumar SSO. */
  blumarOnly?: boolean;
  /** Tipo de la pregunta de columna. Por defecto textField. */
  responseType?: string;
};

export type IncidentSubTableDef = {
  /**
   * Mismo literal que `subsection.subTableKind` en formato y lista plana.
   * Nuevos valores: mismo string en las tres copias de `incidentSubTable.ts` (backend, frontend, app).
   */
  subTableKind: string;
  /** Etiqueta del select en el editor de modelo. */
  editorLabel: string;
  /** Clave del array de filas en el documento incidente. */
  incidentRowsPath: string;
  rowKeyPointers: ReadonlySet<string>;
  columnTemplates: readonly IncidentSubTableColumnTemplate[];
  /**
   * Ensambla filas desde `listaProcedimiento` (agrupa por `dynamicIndex`).
   * Si se omite, se usa `aggregateIncidentSubTableRowsFromListaByColumnTemplates` con `columnTemplates`.
   */
  aggregateRowsFromLista?: (
    lista: any[],
    getIncidentDataFromResponse: (item: any) => any,
    isSubsectionForThisDef: (sub: any) => boolean,
  ) => any[];
  /** Si true, el editor de modelo muestra UI de columnas (`IncidentSubTableConfigForm`) para este kind. */
  hasColumnConfigEditor?: boolean;
  /**
   * Clave `fieldDef.key` en el PDF de incidente / vista previa: resumen de texto y adjuntos por fila
   * desde `incidentRowsPath`. Opcional; si falta, no hay bloque PDF dedicado a este def.
   */
  incidentPdfSummaryFieldKey?: string;
  /**
   * Si se omite, se usa `applyIncidentSubTableRowToProcedureQuestionFromTemplates` con `columnTemplates`.
   */
  applyIncidentRowToProcedureQuestion?: (cloned: any, keyPointer: string, row: any) => void;
};

function rowKeyPointersFromTemplates(
  templates: readonly IncidentSubTableColumnTemplate[],
): ReadonlySet<string> {
  return new Set(templates.map((t) => t.keyPointer));
}

/**
 * Valor de `subsection.subTableKind` / lista plana para la tabla “testigos”.
 * Mismo literal en las tres copias de `incidentSubTable.ts` (backend, frontend, app).
 */
export const INCIDENT_SUBTABLE_KIND_TESTIGOS = 'testigos';

const TESTIGOS_COLUMN_TEMPLATES: readonly IncidentSubTableColumnTemplate[] = [
  {
    id: 'name',
    defaultLabel: 'Nombre',
    keyPointer: 'incident_testigo_nombre',
    rowField: 'name',
  },
  {
    id: 'role',
    defaultLabel: 'Cargo',
    keyPointer: 'incident_testigo_cargo',
    rowField: 'role',
  },
  {
    id: 'contact',
    defaultLabel: 'Contacto',
    keyPointer: 'incident_testigo_contacto',
    rowField: 'contact',
    excludeFromIncidentPdf: true,
  },
  {
    id: 'statement',
    defaultLabel: 'Declaración',
    keyPointer: 'incident_testigo_declaracion',
    rowField: 'statement',
    supportsPhotos: true,
    rowPhotosField: 'statementImages',
  },
];

const TESTIGOS_DEF: IncidentSubTableDef = {
  subTableKind: INCIDENT_SUBTABLE_KIND_TESTIGOS,
  editorLabel: 'Testigos (incidente)',
  incidentRowsPath: 'testigosRows',
  incidentPdfSummaryFieldKey: 'testigos',
  rowKeyPointers: rowKeyPointersFromTemplates(TESTIGOS_COLUMN_TEMPLATES),
  columnTemplates: TESTIGOS_COLUMN_TEMPLATES,
  hasColumnConfigEditor: true,
};

/**
 * `subsection.subTableKind` para la tabla de medidas de control en filas (`medidasControlRows`).
 * Mismo literal en las tres copias de `incidentSubTable.ts` (backend, frontend, app).
 */
export const INCIDENT_SUBTABLE_KIND_MEDIDAS_CONTROL = 'medidasControl';

const MEDIDAS_CONTROL_COLUMN_TEMPLATES: readonly IncidentSubTableColumnTemplate[] = [
  {
    id: 'measure',
    defaultLabel: 'Medida',
    keyPointer: 'incident_medidas_control_medida',
    rowField: 'measure',
  },
  {
    id: 'responsible',
    defaultLabel: 'Responsable',
    keyPointer: 'incident_medidas_control_responsable',
    rowField: 'responsible',
  },
  {
    id: 'position',
    defaultLabel: 'Cargo',
    keyPointer: 'incident_medidas_control_cargo',
    rowField: 'position',
    blumarOnly: true,
  },
  {
    id: 'dueDate',
    defaultLabel: 'Fecha',
    keyPointer: 'incident_medidas_control_fecha',
    rowField: 'dueDate',
    responseType: 'date',
    blumarOnly: true,
  },
  {
    id: 'evidence',
    defaultLabel: 'Evidencia',
    keyPointer: 'incident_medidas_control_evidencia',
    rowField: 'evidence',
    responseType: 'files',
    supportsPhotos: true,
    rowPhotosField: 'evidenceImages',
    blumarOnly: true,
  },
];

const MEDIDAS_CONTROL_DEF: IncidentSubTableDef = {
  subTableKind: INCIDENT_SUBTABLE_KIND_MEDIDAS_CONTROL,
  editorLabel: 'Medidas de control (incidente)',
  incidentRowsPath: 'medidasControlRows',
  incidentPdfSummaryFieldKey: 'medidasControl',
  rowKeyPointers: rowKeyPointersFromTemplates(MEDIDAS_CONTROL_COLUMN_TEMPLATES),
  columnTemplates: MEDIDAS_CONTROL_COLUMN_TEMPLATES,
  hasColumnConfigEditor: true,
};

/**
 * `subsection.subTableKind` para entrevistas de la investigación (`entrevistasRows`).
 * Mismo literal en las tres copias de `incidentSubTable.ts` (backend, frontend, app).
 */
export const INCIDENT_SUBTABLE_KIND_ENTREVISTAS = 'entrevistas';

const ENTREVISTAS_COLUMN_TEMPLATES: readonly IncidentSubTableColumnTemplate[] = [
  {
    id: 'role',
    defaultLabel: 'Rol',
    keyPointer: 'incident_entrevista_rol',
    rowField: 'role',
  },
  {
    id: 'name',
    defaultLabel: 'Nombre',
    keyPointer: 'incident_entrevista_nombre',
    rowField: 'name',
  },
  {
    id: 'experience',
    defaultLabel: 'Experiencia',
    keyPointer: 'incident_entrevista_experiencia',
    rowField: 'experience',
  },
  {
    id: 'workPerformed',
    defaultLabel: 'Trabajo que realizaba',
    keyPointer: 'incident_entrevista_trabajo',
    rowField: 'workPerformed',
  },
];

const ENTREVISTAS_DEF: IncidentSubTableDef = {
  subTableKind: INCIDENT_SUBTABLE_KIND_ENTREVISTAS,
  editorLabel: 'Entrevistas (incidente)',
  incidentRowsPath: 'entrevistasRows',
  incidentPdfSummaryFieldKey: 'entrevistas',
  rowKeyPointers: rowKeyPointersFromTemplates(ENTREVISTAS_COLUMN_TEMPLATES),
  columnTemplates: ENTREVISTAS_COLUMN_TEMPLATES,
  hasColumnConfigEditor: true,
};

/** Orden estable: nuevos kinds aquí; misma lista y orden en stem-web-backend y planiwise-app (`incidentSubTable.ts`). */
export const INCIDENT_SUBTABLE_DEFS: readonly IncidentSubTableDef[] = [
  TESTIGOS_DEF,
  MEDIDAS_CONTROL_DEF,
  ENTREVISTAS_DEF,
];

const defByKind = new Map<string, IncidentSubTableDef>(
  INCIDENT_SUBTABLE_DEFS.map((d) => [d.subTableKind, d]),
);

const strVal = (v: any) => (v != null && v !== '' ? String(v).trim() : '');

/**
 * Cabecera `subseccion` de esta tabla incidente más cercana antes de la pregunta
 * (lista plana hacia atrás; mismo criterio que PDF / tablas dinámicas).
 */
export function resolveIncidentSubTableParentSubseccion(
  arr: any[],
  preguntaIndex: number,
  isSubsectionForThisDef: (sub: any) => boolean,
): any | null {
  for (let j = preguntaIndex - 1; j >= 0; j--) {
    const cand = arr[j];
    if (cand?.tipo === 'seccion') break;
    if (cand?.tipo === 'subseccion' && isSubsectionForThisDef(cand)) {
      return cand;
    }
  }
  return null;
}

/**
 * Agrupa respuestas de `listaProcedimiento` en filas del incidente según plantillas de columna
 * (`keyPointer` → `rowField`, fotos opcionales en `rowPhotosField` o `${rowField}Images`).
 */
export function aggregateIncidentSubTableRowsFromListaByColumnTemplates(
  columnTemplates: readonly IncidentSubTableColumnTemplate[],
  lista: any[],
  getIncidentDataFromResponse: (item: any) => any,
  isSubsectionForThisDef: (sub: any) => boolean,
): any[] {
  const keyToTemplate = new Map(columnTemplates.map((t) => [t.keyPointer, t]));
  const arr = lista || [];
  const rowByDyn = new Map<number, Record<string, any>>();
  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];
    if (item?.tipo !== 'pregunta' || !item.keyPointer) continue;
    if (!keyToTemplate.has(String(item.keyPointer))) continue;

    let sub = resolveIncidentSubTableParentSubseccion(arr, i, isSubsectionForThisDef);
    if (!sub) {
      const subIdx = Number(item.subsectionIndex);
      if (!Number.isFinite(subIdx) || subIdx < 0 || subIdx >= arr.length) continue;
      const direct = arr[subIdx];
      if (direct && direct.tipo === 'subseccion' && isSubsectionForThisDef(direct)) {
        sub = direct;
      }
    }
    if (!sub) continue;

    const dyn = Number(sub.dynamicIndex ?? 0);
    let row = rowByDyn.get(dyn);
    if (!row) {
      row = {};
      rowByDyn.set(dyn, row);
    }
    const tmpl = keyToTemplate.get(String(item.keyPointer));
    if (!tmpl?.rowField) continue;
    const ext = getIncidentDataFromResponse(item);
    const valueFiles = Array.isArray(ext?.value)
      ? ext.value.filter((file: any) => file && (file.publicUrl || file.downloadUrl || file.privateUrl || file.name))
      : [];
    if (!(tmpl.supportsPhotos && valueFiles.length)) {
      row[tmpl.rowField] = strVal(ext?.value);
    }
    if (tmpl.supportsPhotos) {
      const additional = Array.isArray(item.additionalPhotos) ? item.additionalPhotos.filter(Boolean) : [];
      const photos = valueFiles.length ? valueFiles : additional;
      if (photos.length) {
        const pk = tmpl.rowPhotosField ?? `${tmpl.rowField}Images`;
        row[pk] = photos.map((p: any) => ({...p}));
      }
    }
  }
  const dyns = [...rowByDyn.keys()].sort((a, b) => a - b);
  const result: any[] = [];
  for (const d of dyns) {
    const r = rowByDyn.get(d) || {};
    const out: any = {};
    let hasAny = false;
    for (const t of columnTemplates) {
      if (!t.rowField) continue;
      const v = strVal(r[t.rowField]);
      out[t.rowField] = v;
      if (v) hasAny = true;
      if (t.supportsPhotos) {
        const pk = t.rowPhotosField ?? `${t.rowField}Images`;
        const imgs = Array.isArray(r[pk]) ? r[pk] : [];
        if (imgs.length) {
          out[pk] = imgs.map((x: any) => ({...x}));
          hasAny = true;
        }
      }
    }
    if (!hasAny) continue;
    result.push(out);
  }
  return result;
}

/** Prefill de una pregunta clonada desde una fila, según plantilla que coincida con `keyPointer`. */
export function applyIncidentSubTableRowToProcedureQuestionFromTemplates(
  cloned: any,
  keyPointer: string,
  row: any,
  columnTemplates: readonly IncidentSubTableColumnTemplate[],
): void {
  const t = columnTemplates.find((x) => x.keyPointer === keyPointer);
  if (!t?.rowField) return;
  if (t.supportsPhotos && (cloned?.tipoRespuesta === 'files' || cloned?.tipoRespuesta === 'images')) {
    const pk = t.rowPhotosField ?? `${t.rowField}Images`;
    const imgs = Array.isArray(row?.[pk]) ? row[pk] : [];
    applyIncidentPrefillToPreguntaField(cloned, imgs);
    return;
  }
  applyIncidentPrefillToPreguntaField(cloned, String(row?.[t.rowField] ?? ''));
  if (t.supportsPhotos) {
    const pk = t.rowPhotosField ?? `${t.rowField}Images`;
    const imgs = row?.[pk];
    if (Array.isArray(imgs) && imgs.length) {
      cloned.additionalPhotos = imgs.map((p: any) => ({...p}));
    }
  }
}

export function getIncidentSubTableDef(kind: string): IncidentSubTableDef | undefined {
  return defByKind.get(String(kind || ''));
}

export function listIncidentSubTableDefs(): readonly IncidentSubTableDef[] {
  return INCIDENT_SUBTABLE_DEFS;
}

export function isIncidentSubTableProcedureSubsection(item: any): boolean {
  if (!item || item.tipo !== 'subseccion' || !item.isDynamic) return false;
  return defByKind.has(String(item.subTableKind || ''));
}

export function isIncidentSubTableProcedureSubsectionOfKind(item: any, kind: string): boolean {
  return (
    isIncidentSubTableProcedureSubsection(item) &&
    String(item.subTableKind || '') === String(kind || '')
  );
}

/** Def cuyo conjunto `rowKeyPointers` incluye este keyPointer (columna de sub-tabla incidente). */
export function getIncidentSubTableDefForRowKeyPointer(
  keyPointer: string,
): IncidentSubTableDef | undefined {
  const kp = String(keyPointer || '').trim();
  if (!kp) return undefined;
  for (const d of INCIDENT_SUBTABLE_DEFS) {
    if (d.rowKeyPointers.has(kp)) return d;
  }
  return undefined;
}

/**
 * Resuelve la cabecera `subseccion` incident-subtable para la pregunta en `list[preguntaIndex]`:
 * mismo criterio que `aggregateIncidentSubTableRowsFromListaByColumnTemplates` (barrido hacia atrás,
 * con fallback a `list[subsectionIndex]` si coincide el kind).
 */
export function resolveIncidentSubTableSubseccionForPreguntaIndex(
  list: any[],
  preguntaIndex: number,
  def: IncidentSubTableDef,
): any | null {
  const pred = (sub: any) =>
    isIncidentSubTableProcedureSubsectionOfKind(sub, def.subTableKind);
  let sub: any = resolveIncidentSubTableParentSubseccion(list, preguntaIndex, pred);
  if (!sub) {
    const item = list[preguntaIndex];
    const subIdx = Number(item?.subsectionIndex);
    if (Number.isFinite(subIdx) && subIdx >= 0 && subIdx < list.length) {
      const direct = list[subIdx];
      if (direct?.tipo === 'subseccion' && pred(direct)) sub = direct;
    }
  }
  return sub ?? null;
}

/** Unión de keyPointers de fila de todas las tablas de subsección registradas (excluir del reduce plano de incidente). */
export function allRegisteredIncidentSubTableRowKeyPointers(): ReadonlySet<string> {
  const s = new Set<string>();
  for (const d of INCIDENT_SUBTABLE_DEFS) {
    for (const kp of d.rowKeyPointers) s.add(kp);
  }
  return s;
}

export function aggregateIncidentSubTableRowsFromLista(
  lista: any[],
  def: IncidentSubTableDef,
  getIncidentDataFromResponse: (item: any) => any,
): any[] {
  const pred = (sub: any) =>
    isIncidentSubTableProcedureSubsectionOfKind(sub, def.subTableKind);
  if (def.aggregateRowsFromLista) {
    return def.aggregateRowsFromLista(lista, getIncidentDataFromResponse, pred);
  }
  return aggregateIncidentSubTableRowsFromListaByColumnTemplates(
    def.columnTemplates,
    lista,
    getIncidentDataFromResponse,
    pred,
  );
}

export function listaProcedimientoHasSubTableKind(
  lista: any[] | undefined,
  subTableKind: string,
): boolean {
  return (lista || []).some((item: any) =>
    isIncidentSubTableProcedureSubsectionOfKind(item, subTableKind),
  );
}

/** Prefill de una pregunta desde fila incidente; override opcional en el def o plantillas. */
export function applyIncidentSubTableRowPrefillToPregunta(
  cloned: any,
  keyPointer: string,
  row: any,
  def: IncidentSubTableDef,
): void {
  if (def.applyIncidentRowToProcedureQuestion) {
    def.applyIncidentRowToProcedureQuestion(cloned, keyPointer, row);
    return;
  }
  applyIncidentSubTableRowToProcedureQuestionFromTemplates(cloned, keyPointer, row, def.columnTemplates);
}

/**
 * Opciones del select de tipo de tabla de subsección (`subTableKind`) en el editor de modelo (vacío = ninguna).
 * Los `value` deben coincidir con `subTableKind` en las tres copias de `incidentSubTable.ts`.
 */
export const INCIDENT_SUBTABLE_KIND_EDITOR_OPTIONS: {value: string; label: string}[] = [
  {value: '', label: 'Ninguna'},
  ...listIncidentSubTableDefs().map((d) => ({value: d.subTableKind, label: d.editorLabel})),
];

/** Una línea por fila: columnas en orden de plantilla (`rowField`), unidas por " | ". */
export function formatIncidentSubTableRowsAsPlainText(
  rows: unknown,
  def: IncidentSubTableDef,
): string {
  const arr = Array.isArray(rows) ? rows : [];
  const cols = def.columnTemplates.filter((t) => t.rowField);
  return arr
    .map((row: any, idx: number) => {
      const parts = cols.map((t) => String(row?.[t.rowField] ?? '').trim()).filter(Boolean);
      if (!parts.length) return '';
      return `${idx + 1}. ${parts.join(' | ')}`;
    })
    .filter(Boolean)
    .join('\n');
}

export function getIncidentSubTableDefByIncidentRowsPath(
  incidentRowsPath: string,
): IncidentSubTableDef | undefined {
  const p = String(incidentRowsPath || '').trim();
  if (!p) return undefined;
  return listIncidentSubTableDefs().find((d) => d.incidentRowsPath === p);
}

export function getIncidentSubTableDefByPdfSummaryFieldKey(fieldKey: string): IncidentSubTableDef | undefined {
  const k = String(fieldKey || '').trim();
  if (!k) return undefined;
  return listIncidentSubTableDefs().find((d) => d.incidentPdfSummaryFieldKey === k);
}

export function formatIncidentSubTableRowsAsPlainTextByKind(rows: unknown, subTableKind: string): string {
  const def = getIncidentSubTableDef(String(subTableKind || ''));
  if (!def) return '';
  return formatIncidentSubTableRowsAsPlainText(rows, def);
}

export function formatIncidentSubTableRowsAsPlainTextForIncidentRowsPath(
  rows: unknown,
  incidentRowsPath: string,
): string {
  const def = getIncidentSubTableDefByIncidentRowsPath(incidentRowsPath);
  if (!def) return '';
  return formatIncidentSubTableRowsAsPlainText(rows, def);
}

/** Export / listado: una línea por fila según plantillas (`rowField`, fotos opcionales). */
export function formatIncidentSubTableRowsForExport(
  rows: unknown,
  columnTemplates: readonly IncidentSubTableColumnTemplate[],
): string {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  return normalizedRows
    .map((row: any, idx: number) => {
      const parts: string[] = [`#${idx + 1}`];
      for (const t of columnTemplates) {
        if (!t.rowField) continue;
        const v = String(row?.[t.rowField] ?? '').trim();
        if (v) parts.push(v);
        if (t.supportsPhotos) {
          const pk = t.rowPhotosField ?? `${t.rowField}Images`;
          const imgs = Array.isArray(row?.[pk]) ? row[pk] : [];
          const urls = imgs
            .map((f: any) => f?.publicUrl || f?.downloadUrl || '')
            .filter(Boolean)
            .join(' | ');
          if (urls) parts.push(`imgs: ${urls}`);
        }
      }
      return parts.join('; ');
    })
    .filter(Boolean)
    .join('\n');
}

export function formatIncidentSubTableRowsForExportByKind(rows: unknown, subTableKind: string): string {
  const def = getIncidentSubTableDef(String(subTableKind || ''));
  if (!def?.columnTemplates?.length) return '';
  return formatIncidentSubTableRowsForExport(rows, def.columnTemplates);
}

export function formatIncidentSubTableRowsForExportForIncidentRowsPath(
  rows: unknown,
  incidentRowsPath: string,
): string {
  const def = getIncidentSubTableDefByIncidentRowsPath(incidentRowsPath);
  if (!def?.columnTemplates?.length) return '';
  return formatIncidentSubTableRowsForExport(rows, def.columnTemplates);
}

/** Adjuntos en fila (plantillas con `supportsPhotos`) para PDF u otros listados. */
export function collectIncidentSubTableRowPhotosFromRows(rows: unknown, def: IncidentSubTableDef): any[] {
  const arr = Array.isArray(rows) ? rows : [];
  const photoFields = def.columnTemplates.filter((t) => t.supportsPhotos && t.rowField);
  return arr.flatMap((row: any) =>
    photoFields.flatMap((t) => {
      const pk = t.rowPhotosField ?? `${t.rowField}Images`;
      return Array.isArray(row?.[pk]) ? row[pk] : [];
    }),
  );
}

/** Fotos de columnas con `supportsPhotos`, con leyenda tipo PDF de tareas: `Fila n - etiqueta columna`. */
export function collectIncidentSubTablePhotoCellsForPdf(
  rows: unknown,
  def: IncidentSubTableDef,
): {photo: any; caption: string}[] {
  const arr = Array.isArray(rows) ? rows : [];
  const photoTemplates = def.columnTemplates.filter((t) => t.supportsPhotos && t.rowField);
  const cells: {photo: any; caption: string}[] = [];
  arr.forEach((row: any, rowIdx: number) => {
    const rowNum = rowIdx + 1;
    for (const t of photoTemplates) {
      const pk = t.rowPhotosField ?? `${t.rowField}Images`;
      const photos = Array.isArray(row?.[pk]) ? row[pk] : [];
      for (const photo of photos) {
        if (photo) {
          cells.push({
            photo,
            caption: `Fila ${rowNum} - ${t.defaultLabel}`,
          });
        }
      }
    }
  });
  return cells;
}

export type SubTableFieldConfig = {
  /** Coincide con `IncidentSubTableColumnTemplate.id` del def. */
  columnId: string;
  label: string;
  keyPointer: string;
  responseType: string;
  havePhotos?: boolean;
};

export type SubTableConfig = {
  enabled: boolean;
  fields: SubTableFieldConfig[];
};

function resolveSubTableColumnId(raw: any, def: IncidentSubTableDef): string | null {
  const t = String(raw ?? '').trim().toLowerCase();
  if (!t) return null;
  const ids = def.columnTemplates.map((c) => c.id);
  return ids.find((id) => id.toLowerCase() === t) ?? null;
}

export function isIncidentSubTableFormatSubsection(sub: any): boolean {
  if (!sub || !sub.isDynamic) return false;
  return Boolean(getIncidentSubTableDef(String(sub.subTableKind || '')));
}

/** Config por defecto del editor de columnas para un `subTableKind` con `hasColumnConfigEditor`. */
export function buildDefaultSubTableConfigForIncidentSubTable(subTableKind: string): SubTableConfig | null {
  const def = getIncidentSubTableDef(String(subTableKind || ''));
  if (!def?.hasColumnConfigEditor) return null;
  return {
    enabled: true,
    fields: def.columnTemplates
      .filter((tmpl) => !tmpl.blumarOnly)
      .map((tmpl) => ({
      columnId: tmpl.id,
      label: tmpl.defaultLabel,
      keyPointer: tmpl.keyPointer,
      responseType: tmpl.responseType || 'textField',
      ...(tmpl.supportsPhotos ? {havePhotos: true} : {}),
    })),
  };
}

export function normalizeSubTableConfig(subsection: any): SubTableConfig | null {
  if (!isIncidentSubTableFormatSubsection(subsection)) return null;
  const def = getIncidentSubTableDef(String(subsection?.subTableKind || ''));
  if (!def?.hasColumnConfigEditor) return null;
  const raw = subsection?.subTableConfig;
  const rawFields = Array.isArray(raw?.fields) ? raw.fields : [];
  const kind = String(subsection?.subTableKind || '');
  if (!rawFields.length) {
    return buildDefaultSubTableConfigForIncidentSubTable(kind);
  }
  const seen = new Set<string>();
  const fields: SubTableFieldConfig[] = [];
  for (const row of rawFields) {
    const columnId = resolveSubTableColumnId(row?.columnId ?? row?.witnessTarget ?? row?.target, def);
    if (!columnId || seen.has(columnId)) continue;
    seen.add(columnId);
    const tmpl = def.columnTemplates.find((c) => c.id === columnId);
    const defaultLabel = tmpl?.defaultLabel ?? columnId;
    const label =
      String(row?.label ?? row?.pregunta?.titulo ?? row?.pregunta?.label ?? defaultLabel).trim() || defaultLabel;
    fields.push({
      columnId,
      label,
      keyPointer: tmpl?.keyPointer ?? '',
      responseType: tmpl?.responseType || row?.responseType || 'textField',
      ...(tmpl?.supportsPhotos
        ? {havePhotos: row?.havePhotos !== false && row?.havePhotos !== 0}
        : {}),
    });
  }
  if (!fields.length) return buildDefaultSubTableConfigForIncidentSubTable(kind);
  fields.sort(
    (a, b) =>
      def.columnTemplates.findIndex((c) => c.id === a.columnId) -
      def.columnTemplates.findIndex((c) => c.id === b.columnId),
  );
  return {enabled: raw?.enabled !== false, fields};
}

export function buildQuestionsFromSubTableConfig(subsection: any): any[] {
  const config = normalizeSubTableConfig(subsection);
  if (!config?.fields?.length) return [];
  const def = getIncidentSubTableDef(String(subsection?.subTableKind || ''));

  return config.fields.map((field, index) => {
    const pregunta: any = {
      nPregunta: index + 1,
      pregunta: {
        titulo: field.label,
        label: field.label,
      },
      tipoRespuesta: field.responseType || 'textField',
      keyPointer: field.keyPointer,
    };
    const tmpl = def?.columnTemplates.find((c) => c.id === field.columnId);
    if (tmpl?.supportsPhotos && field.havePhotos !== false) {
      pregunta.havePhotos = true;
    }
    return pregunta;
  });
}
